import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { AppProvider } from './contexts/AppContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { UserCacheProvider } from './contexts/UserCacheContext';
import { UsersProvider } from './contexts/UsersContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import ForgotPassword from './components/auth/ForgotPassword';
import CreateAcademy from './components/academy/CreateAcademy';
import DashboardLayout from './components/dashboard/DashboardLayout';
import Dashboard from './components/dashboard/Dashboard';
import Users from './components/users/Users';
import EditUser from './components/users/EditUser';
import UserDetails from './components/users/UserDetails';
import Settings from './components/settings/Settings';
import Finance from './components/finance/Finance';
import PlayerDetails from './components/finance/PlayerDetails';
import GuardianDetails from './components/finance/GuardianDetails';
import Messaging from './components/messaging/Messaging';
import CreditManagement from './components/admin/CreditManagement';

// Import utilities for console access
import { fixUnlinkedCreditsForOrganization } from './services/receiptService';
import { recalculateAllUserBalances } from './services/userService';

// Expose utilities to window for console access
declare global {
  interface Window {
    fixUnlinkedCredits: (orgId?: string) => Promise<any>;
    recalculateFinancialStatus: (orgId?: string) => Promise<void>;
    __selectedOrganizationId?: string;
  }
}

// Wrapper that auto-detects organization ID
window.fixUnlinkedCredits = async (orgId?: string) => {
  const organizationId = orgId || window.__selectedOrganizationId;
  if (!organizationId) {
    console.error('❌ No organization selected. Please select an organization in the app first, or pass the organization ID as a parameter.');
    return null;
  }
  console.log(`🚀 Running fix for organization: ${organizationId}`);
  return fixUnlinkedCreditsForOrganization(organizationId, console.log);
};

// Recalculate financial status for all users
window.recalculateFinancialStatus = async (orgId?: string) => {
  const organizationId = orgId || window.__selectedOrganizationId;
  if (!organizationId) {
    console.error('❌ No organization selected. Please select an organization in the app first, or pass the organization ID as a parameter.');
    return;
  }
  console.log(`🚀 Recalculating financial status for organization: ${organizationId}`);
  return recalculateAllUserBalances(organizationId);
};


function App() {
  return (
    <AuthProvider>
      <PermissionsProvider>
        <SettingsProvider>
          <UserCacheProvider>
            <AppProvider>
              <UsersProvider>
                <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route 
              path="/create-academy" 
              element={
                <ProtectedRoute>
                  <CreateAcademy />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Dashboard />
                  </DashboardLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/users" 
              element={
                <ProtectedRoute requiredResource="users" requiredAction="read">
                  <DashboardLayout>
                    <Users />
                  </DashboardLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/users/:userId" 
              element={
                <ProtectedRoute requiredResource="users" requiredAction="read">
                  <DashboardLayout>
                    <UserDetails />
                  </DashboardLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/users/edit/:userId" 
              element={
                <ProtectedRoute requiredResource="users" requiredAction="write">
                  <DashboardLayout>
                    <EditUser />
                  </DashboardLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/settings" 
              element={
                <ProtectedRoute requiredResource="settings" requiredAction="read">
                  <DashboardLayout>
                    <Settings />
                  </DashboardLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/finance" 
              element={
                <ProtectedRoute requiredResource="finance" requiredAction="read">
                  <DashboardLayout>
                    <Finance />
                  </DashboardLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/finance/player/:playerId" 
              element={
                <ProtectedRoute requiredResource="finance" requiredAction="read">
                  <DashboardLayout>
                    <PlayerDetails />
                  </DashboardLayout>
                </ProtectedRoute>
              } 
            />
            <Route
              path="/finance/guardian/:guardianId"
              element={
                <ProtectedRoute requiredResource="finance" requiredAction="read">
                  <DashboardLayout>
                    <GuardianDetails />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/messaging"
              element={
                <ProtectedRoute requiredResource="messaging" requiredAction="read">
                  <DashboardLayout>
                    <Messaging />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/credits"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <CreditManagement />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<Navigate to="/dashboard" />} />
          </Routes>
                </Router>
              </UsersProvider>
            </AppProvider>
          </UserCacheProvider>
        </SettingsProvider>
      </PermissionsProvider>
    </AuthProvider>
  );
}

export default App;